/**
 * Dual storage adapter — reads from a primary store with localStorage as a
 * fallback, and writes to BOTH so a cutover from localStorage → cookie (or
 * vice versa) doesn't sign anyone out. After ~60 days of dual-write, the
 * fallback can be removed by switching to the primary store directly.
 *
 * Migration timeline:
 *   Phase B (DNS pivot to play.jubit.ai)
 *     ├─ Day 0  : enable cookie primary, dual-write to localStorage
 *     ├─ Day 60 : (optional) drop localStorage fallback once existing
 *     │           sessions have all rotated through refresh
 *     └─ Day 60+: cookie-only
 */
/**
 * Combine two Storage adapters with read-fallback + dual-write semantics.
 * Used to migrate the Supabase session safely between storage backends.
 */
export function createDualStorage(options) {
    const { primary, fallback } = options;
    const preferPrimary = options.preferPrimary ?? true;
    const dualWrite = options.dualWrite ?? true;
    const [read1, read2] = preferPrimary ? [primary, fallback] : [fallback, primary];
    return {
        getItem(key) {
            const v = read1.getItem(key);
            if (v !== null)
                return v;
            return read2.getItem(key);
        },
        setItem(key, value) {
            primary.setItem(key, value);
            if (dualWrite)
                fallback.setItem(key, value);
        },
        removeItem(key) {
            primary.removeItem(key);
            if (dualWrite)
                fallback.removeItem(key);
        },
        clear() {
            primary.clear();
            if (dualWrite)
                fallback.clear();
        },
        key(index) {
            return primary.key(index);
        },
        get length() {
            return primary.length;
        },
    };
}
